import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageCheckIn = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inside');
  const [studentStatus, setStudentStatus] = useState([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    insideCount: 0,
    outsideCount: 0,
    todayEntries: 0,
    todayExits: 0
  });
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterHostel, setFilterHostel] = useState('all');
  const [hostels, setHostels] = useState([]);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualFormData, setManualFormData] = useState({
    studentUserId: '',
    logType: 'entry',
    logDate: new Date().toISOString().split('T')[0],
    logTime: new Date().toTimeString().slice(0, 5),
    gateName: 'Main Gate',
    notes: ''
  });

  useEffect(() => {
    if (userInfo?.userId) {
      fetchData();
      fetchHostels();
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [userInfo]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/superadmin/checkin-status/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setStudentStatus(data.students || []);
        setStats(data.stats || {});
      } else {
        setError(data.message || 'Failed to load data');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
    }
  };

  const handleManualEntry = async () => {
    if (!manualFormData.studentUserId) {
      alert('Please select a student');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/superadmin/checkin-manual/${userInfo.userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(manualFormData)
      });

      const data = await response.json();

      if (data.success) {
        alert('Entry added successfully!');
        setShowManualModal(false);
        resetManualForm();
        fetchData();
      } else {
        alert(data.message || 'Failed to add entry');
      }
    } catch (err) {
      console.error('Error adding manual entry:', err);
      alert('Failed to add entry');
    }
  };

  const resetManualForm = () => {
    setManualFormData({
      studentUserId: '',
      logType: 'entry',
      logDate: new Date().toISOString().split('T')[0],
      logTime: new Date().toTimeString().slice(0, 5),
      gateName: 'Main Gate',
      notes: ''
    });
  };

  const exportToExcel = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/checkin-export/${userInfo.userId}`, {
        method: 'GET',
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `checkin-checkout-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Failed to export data');
    }
  };

  const getFilteredStudents = () => {
    let filtered = studentStatus;

    if (activeTab === 'inside') {
      filtered = filtered.filter(s => s.currentStatus === 'inside');
    } else if (activeTab === 'outside') {
      filtered = filtered.filter(s => s.currentStatus === 'outside');
    }

    if (filterHostel !== 'all') {
      filtered = filtered.filter(s => s.hostelName === filterHostel);
    }

    if (searchQuery) {
      filtered = filtered.filter(s =>
        s.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.phoneNumber?.includes(searchQuery) ||
        s.roomNumber?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredStudents = getFilteredStudents();

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-checkin"
      pageTitle="Check In/Out Management ✅"
    >
      <style>{`
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          border-radius: 20px;
          padding: 24px;
          border: 2px solid rgba(82,171,152,0.15);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          background: rgba(82,171,152,0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin-bottom: 16px;
        }

        .stat-label {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
          margin-bottom: 8px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
        }

        .controls-section {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 50px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #52ab98;
        }

        .filter-select {
          padding: 14px 20px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
        }
        
        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn-primary, .btn-secondary {
          padding: 14px 28px;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          border: none;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s;
        }

        .btn-primary:hover, .btn-secondary:hover {
          transform: translateY(-2px);
        }

        .btn-primary {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(82,171,152,0.2);
        }

        .btn-secondary {
          background: white;
          color: #2b6777;
          border: 2px solid rgba(82,171,152,0.3);
        }

        .tabs-container {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          background: white;
          padding: 8px;
          border-radius: 16px;
          flex-wrap: wrap;
        }

        .tab-btn {
          flex: 1;
          padding: 14px 24px;
          background: transparent;
          color: rgba(43,103,119,0.6);
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .tab-btn.active {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(82,171,152,0.2);
        }

        .students-table {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          border: 2px solid rgba(82,171,152,0.15);
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th {
          padding: 18px 20px;
          text-align: left;
          font-size: 13px;
          font-weight: 800;
          color: #2b6777;
          background: rgba(82,171,152,0.08);
          border-bottom: 2px solid rgba(82,171,152,0.15);
          text-transform: uppercase;
        }

        td {
          padding: 18px 20px;
          font-size: 14px;
          color: #2b6777;
          border-bottom: 1px solid rgba(82,171,152,0.1);
        }
        
        tr:last-child td {
          border-bottom: none;
        }
        
        tr:hover td {
           background: rgba(82,171,152,0.02);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
        }

        .status-inside {
          background: rgba(34,197,94,0.15);
          color: #16a34a;
        }

        .status-outside {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.15);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }
        
        .modal-close:hover {
          background: rgba(82,171,152,0.25);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 32px;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-input, .form-select {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
        }
        
        .form-input:focus, .form-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-cancel, .btn-submit {
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          border: none;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: rgba(107,114,128,0.15);
          color: #374151;
        }
        
        .btn-cancel:hover {
          background: rgba(107,114,128,0.25);
        }

        .btn-submit {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(82,171,152,0.3);
        }
        
        .btn-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(82,171,152,0.4);
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-state-icon {
          font-size: 80px;
          margin-bottom: 20px;
        }

        .empty-state-text {
          font-size: 18px;
          color: #2b6777;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .controls-section {
            flex-direction: column;
            align-items: stretch;
          }
          
          .search-box {
            width: 100%;
          }
          
          .form-row {
            grid-template-columns: 1fr;
          }
          
          .students-table {
            overflow-x: auto;
          }
        }
      `}</style>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-label">Total Students</div>
          <div className="stat-value">{stats.totalStudents || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏠</div>
          <div className="stat-label">Currently Inside</div>
          <div className="stat-value">{stats.insideCount || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🚶</div>
          <div className="stat-label">Currently Outside</div>
          <div className="stat-value">{stats.outsideCount || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-label">Today's Activity</div>
          <div className="stat-value">{(stats.todayEntries || 0) + (stats.todayExits || 0)}</div>
        </div>
      </div>

      <div className="controls-section">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, phone, or room..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterHostel}
          onChange={(e) => setFilterHostel(e.target.value)}
        >
          <option value="all">All Hostels</option>
          {hostels.map(hostel => (
            <option key={hostel.hostel_id} value={hostel.hostel_name}>
              {hostel.hostel_name}
            </option>
          ))}
        </select>

        <div className="action-buttons">
          <button className="btn-primary" onClick={() => setShowManualModal(true)}>
            <span>➕</span> Manual Entry
          </button>
          <button className="btn-secondary" onClick={exportToExcel}>
            <span>📊</span> Export Excel
          </button>
          <button className="btn-secondary" onClick={fetchData}>
            <span>🔄</span> Refresh
          </button>
        </div>
      </div>

      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'inside' ? 'active' : ''}`}
          onClick={() => setActiveTab('inside')}
        >
          🏠 Inside ({stats.insideCount || 0})
        </button>
        <button
          className={`tab-btn ${activeTab === 'outside' ? 'active' : ''}`}
          onClick={() => setActiveTab('outside')}
        >
          🚶 Outside ({stats.outsideCount || 0})
        </button>
        <button
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          📋 All Students
        </button>
      </div>

      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <div className="empty-state-text">Loading data...</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : filteredStudents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <div className="empty-state-text">No students found</div>
        </div>
      ) : (
        <div className="students-table">
          <table>
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Phone</th>
                <th>Hostel</th>
                <th>Room</th>
                <th>Status</th>
                <th>Last Activity</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => (
                <tr key={student.userId}>
                  <td><strong>{student.fullName}</strong></td>
                  <td>{student.phoneNumber}</td>
                  <td>{student.hostelName}</td>
                  <td>{student.roomNumber}</td>
                  <td>
                    <span className={`status-badge ${student.currentStatus === 'inside' ? 'status-inside' : 'status-outside'}`}>
                      {student.currentStatus === 'inside' ? '🏠' : '🚶'} {student.currentStatus}
                    </span>
                  </td>
                  <td>{student.lastLogType === 'entry' ? 'Entry' : 'Exit'}</td>
                  <td>
                    {student.lastLogDate && student.lastLogTime
                      ? `${new Date(student.lastLogDate).toLocaleDateString()} ${student.lastLogTime}`
                      : 'N/A'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showManualModal && (
        <div className="modal-overlay" onClick={() => setShowManualModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add Manual Entry</h2>
              <button className="modal-close" onClick={() => setShowManualModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Student *</label>
                <select
                  className="form-select"
                  value={manualFormData.studentUserId}
                  onChange={(e) => setManualFormData({ ...manualFormData, studentUserId: e.target.value })}
                >
                  <option value="">Select Student</option>
                  {studentStatus.map(student => (
                    <option key={student.userId} value={student.userId}>
                      {student.fullName} - {student.roomNumber} ({student.hostelName})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select
                    className="form-select"
                    value={manualFormData.logType}
                    onChange={(e) => setManualFormData({ ...manualFormData, logType: e.target.value })}
                  >
                    <option value="entry">Entry</option>
                    <option value="exit">Exit</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Gate *</label>
                  <select
                    className="form-select"
                    value={manualFormData.gateName}
                    onChange={(e) => setManualFormData({ ...manualFormData, gateName: e.target.value })}
                  >
                    <option value="Main Gate">Main Gate</option>
                    <option value="Side Gate">Side Gate</option>
                    <option value="Back Gate">Back Gate</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={manualFormData.logDate}
                    onChange={(e) => setManualFormData({ ...manualFormData, logDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Time *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={manualFormData.logTime}
                    onChange={(e) => setManualFormData({ ...manualFormData, logTime: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <input
                  type="text"
                  className="form-input"
                  value={manualFormData.notes}
                  onChange={(e) => setManualFormData({ ...manualFormData, notes: e.target.value })}
                  placeholder="Optional notes..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowManualModal(false)}>
                Cancel
              </button>
              <button className="btn-submit" onClick={handleManualEntry}>
                Add Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default ManageCheckIn;